/**
 * Circle Node Component (New - BaseNode Implementation)
 * Circular node for triggers and special actions
 */

import React from 'react';
import { ZapIcon } from '@bizfirst/common-react';

const PinIcon = ({ size = 10, color = 'white' }: { size?: number; color?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 17v5"/>
    <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76z"/>
  </svg>
);
import { NodeLeftIcon } from '../NodeIcon';
import { NodeProps } from 'reactflow';
import { WorkflowNode, getNodeDisplayProps } from '@flow-studio/types';
import { BaseNodeClass, withNodeHooks, NodeConfig } from './Base/BaseNode';

class CircleNodeClass extends BaseNodeClass {
  protected config(): NodeConfig {
    return {
      ...super.config(),
      showToolbarOnClick: true,
      enablePropertiesModal: true,
      enableRenameModal: true,
      enableMoreMenu: true,
    };
  }

  protected getClassName(): string {
    return 'circle-node';
  }

  protected supportsConfigDimensions(): boolean { return false; }

  protected getContainerStyle(): React.CSSProperties {
    const { selected, data } = this.props;
    const display = getNodeDisplayProps(data);
    const isCollapsed = this.state.isCollapsed;
    const size = isCollapsed ? '60px' : '120px';
    const nodeColor = display.color || '#309B48';

    return {
      width: size,
      height: size,
      borderRadius: '50%',
      background: 'rgba(2, 6, 23, 0.85)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      border: `2px solid ${
        selected
          ? nodeColor
          : data.processElement?.isDisabled
          ? 'rgba(255, 255, 255, 0.20)'
          : 'rgba(255, 255, 255, 0.28)'
      }`,
      opacity: data.processElement?.isDisabled ? 0.6 : 1,
      boxShadow: selected
        ? `0 0 0 3px ${nodeColor}60, 0 0 20px ${nodeColor}40, 0 4px 20px rgba(0, 0, 0, 0.6)`
        : `0 4px 20px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(0, 0, 0, 0.2)`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isCollapsed ? '5px' : '10px',
      position: 'relative',
      cursor: 'move',
    };
  }

  protected renderContent(): React.ReactNode {
    const { data } = this.props;
    const display = getNodeDisplayProps(data);

    return (
      <>
        {/* Icon */}
        <div style={{ marginBottom: '4px', color: 'white', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <NodeLeftIcon icon={display.icon} category={display.category} size={26} color="white" />
        </div>

        {/* Title */}
        <div
          className="bf-node-header-title"
          style={{
            fontSize: '12px',
            fontWeight: 500,
            color: 'rgba(255, 255, 255, 0.9)',
            textAlign: 'center',
            lineHeight: '1.3',
          }}
        >
          {display.displayName}
        </div>

        {/* Execution Status */}
        {display.executionStatus && display.executionStatus !== 'idle' && (
          <div
            style={{
              position: 'absolute',
              bottom: '5px',
              fontSize: '9px',
              padding: '2px 6px',
              borderRadius: '8px',
              background:
                display.executionStatus === 'success'
                  ? 'var(--success-bg)'
                  : display.executionStatus === 'error' || display.executionStatus === 'failed'
                  ? 'var(--error-bg)'
                  : display.executionStatus === 'running'
                  ? 'var(--info-bg)'
                  : 'transparent',
              color:
                display.executionStatus === 'success'
                  ? 'var(--success)'
                  : display.executionStatus === 'error' || display.executionStatus === 'failed'
                  ? 'var(--error)'
                  : display.executionStatus === 'running'
                  ? 'var(--info)'
                  : 'transparent',
            }}
          >
            {display.executionStatus}
          </div>
        )}

        {/* Trigger Badge */}
        {display.isTrigger && (
          <div style={{ position: 'absolute', top: '5px', right: '5px' }}>
            <ZapIcon size={12} color="white" />
          </div>
        )}

        {/* Pin Badge */}
        {data.processElement?.pinnedData && (
          <div title="Output pinned" style={{ position: 'absolute', top: '5px', left: '5px', opacity: 0.85 }}>
            <PinIcon size={10} color="white" />
          </div>
        )}
      </>
    );
  }

  protected onRun(): void {
    console.log('Run step:', this.props.id);
  }

  protected renderCollapsedContent(): React.ReactNode {
    const { data } = this.props;
    const display = getNodeDisplayProps(data);
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <NodeLeftIcon icon={display.icon} category={display.category} size={22} color="white" />
      </div>
    );
  }
}

export const CircleNode: React.FC<NodeProps<WorkflowNode['data']>> =
  withNodeHooks(CircleNodeClass);
